import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { AuthShell } from '@/components/auth/auth-shell';
import { MfaHero } from '@/components/auth/mfa-hero';
import { Button } from '@/components/ui/button';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { getRoleHomeRoute } from '@/lib/role-redirect';
import type { MeProfile, UserRole } from '@/types';
import { getMe, setActivePropertyId, verifyMfa } from '@/services/api.client';

const mfaSchema = z.object({
  code: z
    .string()
    .length(6, 'Enter the 6-digit code from your authenticator app')
    .regex(/^\d{6}$/, 'Code must contain digits only'),
});

type MfaFormValues = z.infer<typeof mfaSchema>;

// Server MFA session TTL is 5 minutes — countdown initialized from challenge issuance time.
const MFA_TTL_SECONDS = 300;

export function MfaPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [secondsLeft, setSecondsLeft] = useState(() => {
    const challengeAt = Number(
      sessionStorage.getItem('secure-gate:mfa-challenge-at') ?? Date.now(),
    );
    const elapsed = Math.floor((Date.now() - challengeAt) / 1000);
    return Math.max(0, MFA_TTL_SECONDS - elapsed);
  });

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const timer = setInterval(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearInterval(timer);
  }, [secondsLeft]);

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;
  const countdown = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<MfaFormValues>({
    resolver: zodResolver(mfaSchema),
    defaultValues: { code: '' },
  });

  const mutation = useMutation({
    mutationFn: ({ code }: MfaFormValues) => verifyMfa(code),
    onSuccess: async (verifyData) => {
      // Clear the challenge timestamp — no longer needed.
      sessionStorage.removeItem('secure-gate:mfa-challenge-at');

      // Set X-Property-Id from the MFA verify response.
      const verifyPropertyId = verifyData.propertyRoles?.[0]?.propertyId ?? null;
      if (verifyPropertyId) {
        setActivePropertyId(verifyPropertyId);
      }

      // Seed the query cache with a profile built from the verify response
      // so AuthProvider sees an authenticated user immediately.
      const now = new Date().toISOString();
      const seedProfile: MeProfile = {
        id: verifyData.userId,
        email: verifyData.email,
        fullName: verifyData.fullName,
        phone: null,
        platformRole: verifyData.platformRole as MeProfile['platformRole'],
        propertyRoles: verifyData.propertyRoles.map((r) => ({
          propertyId: r.propertyId,
          role: r.role as UserRole,
        })),
        status: 'active',
        emailVerified: true,
        mfaEnabled: true,
        lastLoginAt: now,
        createdAt: now,
        updatedAt: now,
      };
      queryClient.setQueryData(['me'], seedProfile);
      void navigate(getRoleHomeRoute(seedProfile));

      // Background refetch for the full profile.
      getMe()
        .then((me) => queryClient.setQueryData(['me'], me))
        .catch(() => {});
    },
  });

  const code = watch('code');

  // Auto-submit when all 6 digits are filled
  useEffect(() => {
    if (code?.length === 6 && /^\d{6}$/.test(code) && !mutation.isPending) {
      void handleSubmit((values) => mutation.mutate(values))();
    }
  }, [code, handleSubmit, mutation]);

  const onSubmit = (values: MfaFormValues) => {
    mutation.mutate(values);
  };

  const isSessionExpired =
    mutation.isError &&
    mutation.error instanceof Object &&
    'status' in mutation.error &&
    (mutation.error as { status: number }).status === 401;

  // 401 → redirect to the standalone AUTH-07 session-expired screen
  useEffect(() => {
    if (isSessionExpired) {
      void navigate('/session-expired');
    }
  }, [isSessionExpired, navigate]);

  const apiError =
    mutation.isError &&
    !isSessionExpired &&
    mutation.error instanceof Object &&
    'message' in mutation.error
      ? String((mutation.error as { message: string }).message)
      : null;

  return (
    <AuthShell
      hero={<MfaHero />}
      title="Verify Your Identity"
      description="Enter the 6-digit code from your authenticator app to complete sign-in."
    >
      <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} noValidate>
        <FieldGroup>
          <Field>
            <FieldLabel>Authenticator Code</FieldLabel>
            <div className="flex justify-center">
              <Controller
                name="code"
                control={control}
                render={({ field }) => (
                  <InputOTP
                    maxLength={6}
                    value={field.value}
                    onChange={field.onChange}
                    autoFocus
                    inputMode="numeric"
                    autoComplete="one-time-code"
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                )}
              />
            </div>
            {errors.code && (
              <p className="text-sm text-destructive text-center" role="alert">
                {errors.code.message}
              </p>
            )}
          </Field>

          {/* Code expiration countdown — amber when < 60 s remain */}
          <div className="flex justify-center gap-1.5 text-sm">
            <span className="text-[var(--text-secondary)]">Code expires in</span>
            <span
              className="font-semibold tabular-nums"
              style={{
                color: secondsLeft <= 60 ? 'var(--status-amber)' : 'var(--text-secondary)',
              }}
            >
              {countdown}
            </span>
          </div>

          {apiError && (
            <p className="text-sm text-destructive text-center" role="alert">
              {apiError}
            </p>
          )}
          <Field>
            <Button type="submit" disabled={mutation.isPending || (code?.length ?? 0) < 6}>
              {mutation.isPending ? 'Verifying…' : 'Verify Code'}
            </Button>
          </Field>
          <div className="flex flex-col items-center gap-1 text-sm text-[var(--text-secondary)]">
            <p>
              Didn&apos;t receive the code?{' '}
              <Link
                to="/login"
                className="underline underline-offset-4 hover:text-[var(--text-accent)]"
              >
                Back to Sign In
              </Link>
            </p>
          </div>
        </FieldGroup>
      </form>
    </AuthShell>
  );
}
